/**
 * The run table's filters, order and bulk actions.
 *
 * What the funnel holds is what somebody ticked and typed; what travels is `RunFilters`, applied
 * upstream. A filter or an order applied here would only reach the page on screen, and the count
 * under the table would stop matching what is in it — so both travel, and this is where the funnel
 * is translated into what Virtual Profiler takes.
 *
 * ## Two bulk actions, never one
 *
 * «Quitar de la cola» and «Cancelar» are different decisions about different runs: one never
 * started and leaves no trace, the other is consuming a slot and stops at its next checkpoint. A
 * single «parar las seleccionadas» would hide which of the two each run got, which is the mistake
 * the row's own buttons are built to prevent. Which run takes which is `availableActions`, the rule
 * those buttons follow, so the bar and the row cannot disagree about a run.
 */
import type { SortDir } from '@beonit/adaptive-raindrop';
import { ApiError, ApiErrorCode } from '@shared/infrastructure/http/api-error.js';
import type { BulkRunOutcome } from '../api/analysis-runs.api.js';
import type { AnalysisRun, ControlKind, RunFilters, RunSourceType } from '../model.js';
import { formatCount } from './format-count.js';
import { isControlKind } from './group-owner.js';
import type { SortOption } from './list-sort.js';
import { availableActions } from './queue-presentation.js';
import { RUN_STATUS_FILTERS, type RunStatusFilter, statusesFor } from './queue-rows.js';

// ── Order ────────────────────────────────────────────────────────────────────

/** The three instants of a run the list endpoint can order by. */
export type RunSortField = 'enqueued_at' | 'started_at' | 'finished_at';

export type RunSortOption = SortOption<RunSortField>;

function order(field: RunSortField, dir: SortDir, label: string): RunSortOption {
  return { key: `${field}:${dir}`, label, orderBy: field, orderDir: dir };
}

/**
 * The orders the table offers. The first is the default and what Virtual Profiler does unasked:
 * the newest launch first, which is the question the screen answers most — «what just went in».
 */
export const RUN_SORTS: readonly RunSortOption[] = Object.freeze([
  order('enqueued_at', 'desc', 'Lanzadas: más recientes'),
  order('enqueued_at', 'asc', 'Lanzadas: más antiguas'),
  order('started_at', 'desc', 'Empezadas: más recientes'),
  order('started_at', 'asc', 'Empezadas: más antiguas'),
  order('finished_at', 'desc', 'Terminadas: más recientes'),
  order('finished_at', 'asc', 'Terminadas: más antiguas'),
]);

export const DEFAULT_RUN_SORT = RUN_SORTS[0]!.key;

// ── Filters ──────────────────────────────────────────────────────────────────

/** How Virtual Profiler spells «sin grupo» in a query string. */
export const NO_GROUP = 'null';

/** What the funnel holds, as ticked and typed. */
export interface RunFilterState {
  /** None ticked is every state. */
  states: RunStatusFilter[];
  /** The select's value: `''` is «cualquier origen». */
  sourceType: '' | RunSourceType;
  /** The source id as typed — a video's id for the runs of the gallery. */
  video: string;
  /**
   * The select's value: `''` is «cualquier grupo», `NO_GROUP` is «sin grupo», a control's is
   * `controlFilterValue(kind)` (`control:gestures`), else a group's id.
   */
  group: string;
}

const CONTROL_FILTER_PREFIX = 'control:';

/** The «Grupo» select's value for a control: a control is not a group, and travels apart. */
export function controlFilterValue(kind: ControlKind): string {
  return `${CONTROL_FILTER_PREFIX}${kind}`;
}

/** The control a «Grupo» select value names, or null for a group's id, «sin grupo» or none. */
export function controlFilterKind(value: string): ControlKind | null {
  const kind = value.trim().startsWith(CONTROL_FILTER_PREFIX)
    ? value.trim().slice(CONTROL_FILTER_PREFIX.length)
    : null;

  return isControlKind(kind) ? kind : null;
}

export const EMPTY_RUN_FILTERS: Readonly<RunFilterState> = Object.freeze({
  states: [],
  sourceType: '',
  video: '',
  group: '',
});

/**
 * The table's quick filter: only what is running now.
 *
 * Off when the table opens, which shows every run. It is the funnel's own «Estado», not a second
 * filter beside it: pressing it ticks exactly «en ejecución» and leaves origin, video and group
 * alone, and it reads pressed whenever that is exactly what is ticked, however it got there.
 */
export function isRunningOnly(state: RunFilterState): boolean {
  return state.states.length === 1 && state.states[0] === 'running';
}

/** Pressing the quick filter: on, only what is running; off, every state. */
export function toggleRunningOnly(state: RunFilterState): RunFilterState {
  return { ...state, states: isRunningOnly(state) ? [] : ['running'] };
}

/**
 * The funnel, as the filters that travel: only what narrows.
 *
 * Every state ticked is no state at all — it matches every run, so it travels as nothing and the
 * funnel's badge does not claim it narrows anything. «Sin grupo» travels as the word `null`, which
 * is how Virtual Profiler reads it: an empty value is dropped from the query string and would ask
 * for every run instead of the ones without a group.
 */
export function toRunFilters(state: RunFilterState): RunFilters {
  const filters: RunFilters = {};

  const narrowsStates = new Set(state.states).size < RUN_STATUS_FILTERS.length;
  const status = narrowsStates ? statusesFor(state.states) : undefined;
  if (status) filters.status = status;

  if (state.sourceType) filters.sourceType = state.sourceType;

  const video = state.video.trim();
  if (video) filters.sourceId = video;

  const group = state.group.trim();
  const control = controlFilterKind(group);
  if (control) filters.controlKind = control;
  else if (group) filters.groupId = group;

  return filters;
}

/** How many kinds of filter are narrowing the list, for the funnel's badge: what travels. */
export function activeRunFilterCount(state: RunFilterState): number {
  return Object.keys(toRunFilters(state)).length;
}

export const TYPING_DEBOUNCE_MS = 300;

/**
 * How long a change to the funnel waits before it travels.
 *
 * Typing a video id waits for a pause: «1», «10», «101» are three requests nobody wanted, and on a
 * live screen each one repaints the table under the cursor. Anything picked — a state, an origin, a
 * group — travels at once because nothing more is coming, and so does emptying the field, which is
 * how somebody undoes a filter and expects to see everything again.
 */
export function filterDelay(previous: RunFilterState, next: RunFilterState): number {
  const typed = previous.video !== next.video && next.video.trim() !== '';
  const picked =
    previous.sourceType !== next.sourceType ||
    previous.group !== next.group ||
    previous.states.length !== next.states.length ||
    previous.states.some((state, index) => next.states[index] !== state);

  return typed && !picked ? TYPING_DEBOUNCE_MS : 0;
}

// ── Paging ───────────────────────────────────────────────────────────────────

/**
 * The page to stay on once an answer says how many pages there are now.
 *
 * A narrowed list shrinks under a live screen — a bulk «quitar de la cola» from page 3 of «en
 * cola», or runs finishing under «en ejecución» — and the page on screen can end up past the last
 * one. Left there, the table would say «ninguna ejecución casa con estos filtros» above a footer
 * counting twelve, so it steps back to the last page there is. No pages at all is page 1, where
 * that empty state is true.
 */
export function pageWithin(page: number, totalPages: number): number {
  return Math.max(1, Math.min(page, totalPages));
}

// ── Bulk actions ─────────────────────────────────────────────────────────────

export type BulkRunAction = 'withdraw' | 'cancel';

/**
 * Whether a run can be taken out of the queue: it has not started. The rule of the row's own
 * button, read from the same place.
 */
export function withdrawable(run: AnalysisRun): boolean {
  return availableActions(run, { now: Date.now() }).withdraw;
}

/**
 * Whether a run can be asked to stop: it is running. Not a run already asked to stop — asking again
 * changes nothing, and forcing it is a decision for the row, one run at a time.
 */
export function cancellable(run: AnalysisRun): boolean {
  return availableActions(run, { now: Date.now() }).cancel;
}

const ELIGIBLE: Readonly<Record<BulkRunAction, (run: AnalysisRun) => boolean>> = Object.freeze({
  withdraw: withdrawable,
  cancel: cancellable,
});

/** The selected runs one action applies to, in the order they were selected. */
export function bulkTargets(action: BulkRunAction, runs: readonly AnalysisRun[]): AnalysisRun[] {
  return runs.filter((run) => ELIGIBLE[action](run));
}

/**
 * What to send, re-read against the freshest rows at the moment of sending.
 *
 * Asked, confirmed and sent are three moments, and the socket moves runs between any two of them.
 * A run that started since the dialog opened is not sent to be withdrawn — upstream would refuse it
 * — and is counted with the ones that got away (`moved`). A run no longer known at all is left out
 * of both: it was pruned from the selection meanwhile, and there is nothing true to say about it.
 */
export function recheck(
  action: BulkRunAction,
  runIds: readonly string[],
  freshest: readonly AnalysisRun[],
): { send: string[]; moved: string[] } {
  const byId = new Map(freshest.map((run) => [run.id, run] as const));
  const send: string[] = [];
  const moved: string[] = [];

  for (const runId of runIds) {
    const run = byId.get(runId);
    if (!run) continue;
    (ELIGIBLE[action](run) ? send : moved).push(runId);
  }

  return { send, moved };
}

/** How a bulk action went, run by run. */
export interface BulkTally {
  done: string[];
  /** Answered 409: it had already started, or already finished. Not a failure of the action. */
  conflict: string[];
  failed: string[];
  /** The first real failure, for the message: the rest are usually the same one. */
  firstFailure: unknown;
}

/**
 * Whether a refusal only means the run moved on before the request landed.
 *
 * By status and not by code: upstream answers a withdraw of a started run, a cancel of a finished
 * one and a cancel of one saving its result with three codes, all 409, and all three are «it got
 * there first» rather than something going wrong.
 */
export function movedOnBeforehand(error: unknown): boolean {
  return error instanceof ApiError && error.status === 409;
}

/**
 * Acts on several runs in one call (2026-10-29), and says how each one went, as `settleEach` does.
 * The back-office answers one outcome per run; a failure of the call itself fails every run with
 * it. Never throws.
 */
export async function settleMany(
  runIds: readonly string[],
  act: (runIds: readonly string[]) => Promise<readonly BulkRunOutcome[]>,
): Promise<BulkTally> {
  const tally: BulkTally = { done: [], conflict: [], failed: [], firstFailure: undefined };

  let outcomes: readonly BulkRunOutcome[];
  try {
    outcomes = await act(runIds);
  } catch (error) {
    return { ...tally, failed: [...runIds], firstFailure: error };
  }

  for (const item of outcomes) {
    if (item.outcome === 'done') {
      tally.done.push(item.run_id);
    } else if (item.outcome === 'conflict') {
      tally.conflict.push(item.run_id);
    } else {
      tally.failed.push(item.run_id);
      tally.firstFailure ??=
        item.outcome === 'missing'
          ? new ApiError(ApiErrorCode.VP_RUN_NOT_FOUND, item.reason ?? '', 404)
          : new ApiError(item.code ?? 'UNKNOWN', item.reason ?? '', 500);
    }
  }

  return tally;
}

/**
 * Acts on each run in turn, and says how each one went.
 *
 * One at a time because Virtual Profiler has no bulk endpoint, and in turn rather than all at once
 * so fifty requests do not land together on the process that is also dispatching the queue. Never
 * throws: a failure is one run's outcome, and the runs after it are still attempted.
 */
export async function settleEach(
  runIds: readonly string[],
  act: (runId: string) => Promise<unknown>,
): Promise<BulkTally> {
  const tally: BulkTally = {
    done: [],
    conflict: [],
    failed: [],
    firstFailure: undefined,
  };

  for (const runId of runIds) {
    try {
      await act(runId);
      tally.done.push(runId);
    } catch (error) {
      if (movedOnBeforehand(error)) {
        tally.conflict.push(runId);
      } else {
        tally.failed.push(runId);
        tally.firstFailure ??= error;
      }
    }
  }

  return tally;
}

function plural(count: number, one: string, many: string): string {
  return `${formatCount(count)} ${count === 1 ? one : many}`;
}

export interface BulkSummary {
  tone: 'success' | 'warning' | 'error';
  text: string;
}

/**
 * The toast after a bulk action: every outcome counted, none rounded into «hecho».
 *
 * «Hecho» after withdrawing 47 of 50 would be a lie about the three now running. A cancellation
 * says it was asked for, not that it happened: it lands at the run's next checkpoint, and promising
 * it is done would send somebody looking for a stopped run that is still going.
 */
export function bulkSummary(
  action: BulkRunAction,
  outcome: { done: number; conflict: number; failed: number },
): BulkSummary {
  const parts =
    action === 'withdraw'
      ? [
          `${plural(outcome.done, 'quitada', 'quitadas')} de la cola`,
          outcome.conflict > 0
            ? `${plural(outcome.conflict, 'ya había', 'ya habían')} empezado`
            : null,
          outcome.failed > 0
            ? `${plural(outcome.failed, 'no se ha', 'no se han')} podido quitar`
            : null,
        ]
      : [
          `Cancelación pedida para ${formatCount(outcome.done)}`,
          outcome.conflict > 0
            ? `${plural(outcome.conflict, 'ya no estaba', 'ya no estaban')} en marcha`
            : null,
          outcome.failed > 0
            ? `${plural(outcome.failed, 'no se ha', 'no se han')} podido cancelar`
            : null,
        ];

  let tone: BulkSummary['tone'] = 'success';
  if (outcome.conflict > 0) tone = 'warning';
  if (outcome.failed > 0) tone = 'error';

  return { tone, text: parts.filter((part) => part !== null).join(' · ') };
}

export interface BulkConfirmation {
  title: string;
  description: string;
  actionLabel: string;
}

/** The selected runs an action leaves alone, with the verb agreeing with how many there are. */
function untouched(others: number, where: string): string | null {
  if (others <= 0) return null;

  return others === 1
    ? `La otra seleccionada no está ${where} y no se toca.`
    : `Las otras ${formatCount(others)} seleccionadas no están ${where} y no se tocan.`;
}

/**
 * The confirmation of a bulk action, with the count it is about to act on.
 *
 * `selected` is how many are selected in all: the rest are named too, so nobody reads «3» beside a
 * selection of ten and wonders what happens to the other seven. One run is spoken of as one: a
 * dialog titled «1 ejecución» that goes on about «cada una» reads as a count that is wrong.
 */
export function bulkConfirmation(
  action: BulkRunAction,
  count: number,
  selected: number,
): BulkConfirmation {
  const others = selected - count;
  const one = count === 1;

  if (action === 'withdraw') {
    return {
      title: `¿Quitar de la cola ${plural(count, 'ejecución', 'ejecuciones')}?`,
      description: [
        one ? 'Sale de la cola antes de empezar.' : 'Salen de la cola antes de empezar.',
        'No se borra nada: el histórico de análisis no se toca.',
        one
          ? 'Si empieza antes de que llegue la petición, sigue su curso.'
          : 'Se quitan una a una, así que alguna puede empezar entretanto; esa sigue su curso.',
        untouched(others, 'en cola'),
      ]
        .filter((line) => line !== null)
        .join(' '),
      actionLabel: 'Quitar de la cola',
    };
  }

  return {
    title: `¿Cancelar ${formatCount(count)} análisis en marcha?`,
    description: [
      one
        ? 'Se detiene en su siguiente punto de control, así que puede tardar un momento. Su vídeo vuelve a su estado anterior y no queda marcado como fallido.'
        : 'Cada uno se detiene en su siguiente punto de control, así que puede tardar un momento. Sus vídeos vuelven a su estado anterior y no quedan marcados como fallidos.',
      untouched(others, 'en ejecución'),
    ]
      .filter((line) => line !== null)
      .join(' '),
    actionLabel: 'Cancelar los análisis',
  };
}

/**
 * What a bulk button says on hover. Disabled, why; enabled, which of the selected it acts on — the
 * two buttons act on different runs of the same selection, and that is the thing to be sure of.
 */
export function bulkButtonTitle(action: BulkRunAction, count: number): string {
  if (action === 'withdraw') {
    return count === 0
      ? 'Ninguna de las seleccionadas está en cola'
      : 'Quita de la cola las seleccionadas que aún no han empezado';
  }

  return count === 0
    ? 'Ninguna de las seleccionadas está en ejecución'
    : 'Pide parar las seleccionadas que están en ejecución';
}
